<template>
  <div>
    <loading-overlay v-if="loading" />

    <title-row
      :title="page.title"
      :subtitle="page.subtitle"
      :breadcrumb-items="[
        {
          text: 'Kártyák',
          to: { name: 'CardList' },
        },
        page.breadcrumbItem,
      ]"
    >
      <template #buttons>
        <title-row-button
          v-if="form.status.name_ === 'printReady' && showPrintIcon"
          @click="print"
          :loading="saving"
        >
          <v-icon left>mdi-printer</v-icon>
          Nyomtatás
        </title-row-button>
        <title-row-button v-if="form.status.name_ === 'active'" @click="suspend" :loading="saving">
          <v-icon left>mdi-account-clock</v-icon>
          Felfüggesztés
        </title-row-button>
        <title-row-button
          v-if="form.status.name_ === 'active' || form.status.name_ === 'tempInactive'"
          @click="deactivate"
          :loading="saving"
        >
          <v-icon>mdi-account-off</v-icon>
          Végleges letiltás
        </title-row-button>
        <title-row-button
          v-if="form.status.name_ === 'tempInactive'"
          @click="activate"
          :loading="saving"
        >
          <v-icon>mdi-account-reactivate</v-icon>
          Aktiválás
        </title-row-button>
        <title-row-button
          v-if="form.status.name_ !== 'permInactive'"
          @click="save"
          :loading="saving"
        >
          <v-icon left>mdi-check</v-icon>
          Mentés
        </title-row-button>
      </template>
    </title-row>

    <v-card class="rounded-xl overflow-hidden" flat>
      <v-tabs v-model="tab" align-tabs="center" color="text--primary">
        <v-tabs-slider color="primary"></v-tabs-slider>
        <v-tab key="meta"> Metaadatok </v-tab>
        <v-tab key="parking" v-if="!page.isNew"> Parkolás </v-tab>
        <v-tab key="assets" v-if="!page.isNew"> Eszközök </v-tab>
        <v-tab key="access_log" v-if="!page.isNew"> Belépés napló </v-tab>
        <v-tab key="system_log" v-if="!page.isNew"> Rendszer napló </v-tab>
        <v-tab key="access_check" v-if="!page.isNew"> Hibakeresés </v-tab>

        <v-tabs-items v-model="tab">
          <v-tab-item key="meta" :transition="false">
            <v-row dense>
              <v-col cols="12" lg="6">
                <v-card flat>
                  <v-card-title>Kártya adatok</v-card-title>
                  <v-card-text>
                    <v-masked-text-field
                      label="Kártyaszám"
                      v-model="form.card_number"
                      :mask="$config.inputMasks.cardNumber"
                      filled
                    />

                    <v-card flat color="muted" class="px-4 mb-8 d-flex align-center">
                      <v-switch
                        label="Aktív kártya"
                        class="py-2 my-2"
                        hide-details
                        v-model="form.active"
                        inset
                      />
                    </v-card>

                    <v-masked-text-field
                      label="RFID"
                      v-model="form.rf_id"
                      :mask="$config.inputMasks.rfId"
                      persistent-hint
                      placeholder="Érintse a kártyát az olvasóhoz"
                      filled
                      :error-messages="errors.rf_id"
                      v-uppercase
                      :hint="
                        form.status.name_ && form.status.name_ !== 'printReady'
                          ? 'Nem módosítható'
                          : ''
                      "
                    />
                    <v-text-field
                      label="Státusz"
                      :value="form.status.name"
                      readonly
                      hint="Nem módosítható"
                      persistent-hint
                      filled
                    />
                    <v-autocomplete
                      v-if="hasFeature('CARD_TEMPLATE')"
                      label="Sablon"
                      v-model="form.template_id"
                      :items="cardTemplates"
                      item-text="name"
                      item-value="id"
                      @change="showPrintIcon = false"
                      :readonly="form.status.name_ && form.status.name_ !== 'printReady'"
                      :error-messages="errors.template_id"
                      :hint="
                        form.status.name_ && form.status.name_ !== 'printReady'
                          ? 'Nem módosítható'
                          : ''
                      "
                      persistent-hint
                      filled
                    />
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" lg="6">
                <v-card flat>
                  <v-card-title>Birtokos adatok</v-card-title>
                  <v-card-text>
                    <v-select
                      label="Birtokos típus"
                      :items="[
                        { text: 'Új birtokos létrehozása', value: 'new' },
                        { text: 'Létező birotokoshoz rendelés', value: 'existing' },
                      ]"
                      filled
                      v-model="cardOwnerType"
                    />

                    <card-owner-form
                      v-if="cardOwnerType === 'new'"
                      :card-owner="form.owner"
                      :errors="errors.owner"
                    />
                    <template v-else>
                      <async-autocomplete
                        label="Név"
                        v-model="form.owner"
                        url="card-owners/list"
                        item-value="element_id"
                        item-text="name"
                        array-property="card_owners"
                        filled
                        return-object
                      >
                        <template #item="{ item }">
                          <div class="py-2">
                            <div>{{ item.name }}</div>
                            <div class="caption">{{ item.email }}</div>
                          </div>
                        </template>
                      </async-autocomplete>
                      <card-owner-form
                        :card-owner="form.owner"
                        :errors="errors.owner"
                        :hide="['name']"
                      />
                    </template>

                    <!-- <dynamic-field
                      v-for="field in extraFields"
                      :key="`extra-field-${field.name}`"
                      :field="field"
                      v-model="form.extra_fields[field.name]"
                      :error-messages="errors[field.name]"
                      filled
                    /> -->
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-card flat>
                  <v-card-title>Hozzáférési szintek</v-card-title>
                  <v-card-text>
                    <v-multi-select-list
                      :headers="[
                        { text: 'Megnevezés', value: 'name' },
                        { text: 'Lejárat', value: 'valid_to' },
                      ]"
                      :items="accessLevels"
                      v-model="form.access_levels"
                      item-value="element_id"
                      item-text="name"
                      unique-values
                      :blank-item-template="{ element_id: 0 }"
                    >
                      <template #[`item.name`]="{ item, items }">
                        <v-autocomplete
                          :items="items"
                          v-model="item.element_id"
                          item-text="name"
                          item-value="element_id"
                          placeholder="Válassz"
                          hide-details
                          filled
                          dense
                        />
                      </template>
                      <template #[`item.valid_to`]="{ item }">
                        <v-datetime-picker
                          v-model="item.valid_to"
                          :time-picker="false"
                          hide-details
                          dense
                          placeholder="Nincs lejárat"
                          filled
                        />
                      </template>
                    </v-multi-select-list>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-tab-item>

          <v-tab-item key="parking" :transition="false">
            <v-card flat>
              <v-card-title>Rendszámok</v-card-title>
              <v-card-text>
                <v-multi-select-list
                  :headers="[{ text: 'Rendszám', value: 'number' }]"
                  :items="form.plates"
                  v-model="form.plates"
                  item-value="number"
                  item-text="number"
                  hide-default-header
                >
                  <template #[`item.number`]="{ item, index }">
                    <v-masked-text-field
                      :label="`Rendszám ${index + 1}`"
                      v-model="item.number"
                      :error-messages="errors[`plate-${index}`]"
                      filled
                      dense
                      :hide-details="!item.active_season_ticket"
                      v-uppercase
                      :mask="$config.inputMasks.plateNumber"
                    />
                  </template>
                </v-multi-select-list>
              </v-card-text>
            </v-card>

            <v-divider />

            <v-card flat>
              <v-card-title>Parkolási büntetések</v-card-title>
              <v-card-text>
                <parking-penalties />
              </v-card-text>
            </v-card>
          </v-tab-item>

          <v-tab-item key="assets" :transition="false">
            <v-card flat>
              <v-card-title>
                <div class="d-flex align-center">
                  <span>Tulajdonodban</span>
                  <v-chip
                    v-if="assets && assets.length"
                    small
                    outlined
                    class="ml-2"
                    color="primary"
                  >
                    {{ assets.length }} db
                  </v-chip>
                </div>
              </v-card-title>

              <v-card-text>
                <v-row v-if="assets && assets.length">
                  <v-col
                    v-for="asset in assets"
                    :key="asset.id"
                    :cols="12"
                    :sm="6"
                    :md="4"
                    :lg="3">
                    <v-card rounded="lg" flat outlined>
                      <v-card-text>
                        <div class="d-flex align-center">
                          <v-sheet
                            :color="asset.color || 'grey'"
                            width="4"
                            height="32"
                            rounded
                            class="mr-3"
                          />
                          <div class="flex-grow-1">
                            <div class="font-weight-bold text-truncate">
                              {{ asset.name }}
                            </div>
                          </div>
                          <v-btn
                            v-if="hasRight('KEY_WRITE')"
                            icon
                            small
                            color="primary"
                            :to="{ name: 'AssetEditor', params: { id: asset.id } }"
                            title="Szerkesztés"
                          >
                            <v-icon small>mdi-pencil</v-icon>
                          </v-btn>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <v-card v-else flat class="pa-6">
                  <div class="text--secondary">Jelenleg nincs kulcs a tulajdonodban</div>
                </v-card>
              </v-card-text>
            </v-card>

            <v-card flat>
              <v-card-title>
                <div class="d-flex align-center">
                  <span>Összes kulcs</span>
                  <v-chip
                    v-if="dataTable.itemsLength"
                    color="primary"
                    small
                    outlined
                    class="ml-2">
                    {{ dataTable.itemsLength }} db
                  </v-chip>
                </div>
              </v-card-title>

              <v-card-text>
                <v-row class="mb-2" justify="start">
                  <v-col
                    :cols="12"
                    :sm="12"
                    :md="6"
                    :lg="3">
                    <v-text-field
                      v-model="assetSearch.name"
                      label="Keresés..."
                      dense
                      clearable
                      hide-details
                      filled
                      color="primary"
                      class="search-field"
                      @input="searchEventHandler"
                      v-mask="$config.inputMasks.name"
                    />
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="dataTable.headers"
                  :items="dataTable.items"
                  :server-items-length="dataTable.itemsLength"
                  :options.sync="dataTable.options"
                  :footer-props="$config.dataTableFooterProps"
                  @update:options="fetchItems"
                  elevation="2"
                  class="rounded-lg"
                >
                  <template #[`item.name`]="{ item }">
                    <div class="d-flex align-center">
                      <v-sheet
                        :color="`${item.color || 'grey'}`"
                        width="8"
                        height="24"
                        rounded="sm"
                        class="mr-2"
                      />
                      <span class="font-weight-bold">{{ item.name }}</span>
                    </div>
                  </template>

                  <template #[`item.cabinets`]="{ item }">
                    <v-chip
                      v-for="cabinet in item.cabinets"
                      :key="cabinet.id"
                      label
                      link
                      small
                      :to="{
                        name: 'AssetCabinetList',
                        params: { highlight: [cabinet.id] },
                      }"
                      class="ma-1"
                    >
                      {{ cabinet.name }}
                    </v-chip>
                  </template>

                  <template #[`item.actions`]="{ item }">
                    <v-btn
                      v-if="hasRight('KEY_WRITE')"
                      text
                      fab
                      small
                      dark
                      color="primary"
                      class="rounded-sm mr-1"
                      :to="{ name: 'AssetEditor', params: { id: item.id } }"
                      title="Szerkesztés"
                    >
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <v-tab-item key="access_log" :transition="false">
            <v-card flat>
              <v-card-title>Belépés napló</v-card-title>
              <v-card-text>
                <access-log :card-id="form.element_id" />
              </v-card-text>
            </v-card>
          </v-tab-item>

          <v-tab-item key="system_log" :transition="false">
            <v-card flat>
              <v-card-title>Rendszer napló</v-card-title>
              <v-card-text>
                <admin-log
                  :card-id="form.element_id"
                  :access-levels="accessLevels"
                  :card-statuses="cardStatuses"
                />
              </v-card-text>
            </v-card>
          </v-tab-item>

          <v-tab-item key="access_check" :transition="false">
            <v-card flat>
              <v-card-title>Hibakeresés</v-card-title>
              <v-card-text> </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </v-card>
  </div>
</template>

<script>
import print from 'print-js';
import AsyncAutocomplete from '@/components/AsyncAutocomplete.vue';
import CardOwnerForm from '@/components/forms/CardOwner.vue';
import { cardReader, dataTablePage } from '@/mixins';

export default {
  mixins: [cardReader, dataTablePage],

  components: {
    CardOwnerForm,
    AsyncAutocomplete,
    AdminLog: () => import('./AdminLog.vue'),
    AccessLog: () => import('./AccessLog.vue'),
    ParkingPenalties: () => import('@/components/ParkingPenalties.vue'),
  },

  data() {
    return {
      tab: 'meta',
      cardOwnerType: 'new',
      form: {
        active: 1,
        status: 'idReading',
        owner: {},
        access_levels: [],
        plates: [],
      },
      formSnapshot: null,
      newAccessLevelId: 0,
      newAccessLevels: [],
      cardOwners: [],
      logs: [],
      accessLevels: [],
      cardStatuses: [],
      assets: [],
      defaultStatus: {},
      cardAccessLevelsGroups: [],
      cardTemplates: [],
      defaultTemplate: {},
      showPrintIcon: true,
      parkingPenalty: {
        start_date: '',
        interval: '1 MONTH',
        reason: '',
      },
      errors: {},
      loading: false,
      saving: false,
      socket: '',
      headers: [
        { text: 'Adminisztrátor', value: 'writer_name', sortable: false },
        { text: 'Módosítva', value: 'created', sortable: false },
        { value: 'v', sortable: false },
      ],
      search: {
        timeout: null,
      },
      neptunCourses: null,
      assetSearch: {
        name: '',
        taken: null,
        asset_cabinet_id: '',
        building_id: '',
        building_level_id: '',
      },
    };
  },

  beforeDestroy() {
    this.closeCardReaderSocket();
  },

  mounted() {
    this.watchCardReader(async (event) => {
      if (event.data) {
        console.log(event.data);
        if (event.data === 'NO_READERS') {
          const result = this.$dialog.error({
            text: 'A kártyaolvasó nem található. Kérjük ellenőrizze a csatlakozást, vagy jelentse a karbantartónak!',
            title: 'Figyelem!',
            color: 'error',
          });
          return;
        }
        const result = await this.$dialog.confirm({
          text: 'A megfelelő kártyát érintette az olvasóhoz?',
          title: 'Figyelem!',
          color: 'primary',
        });
        if (result) {
          this.form.rf_id = event.data;
        }
      }
    });
    this.fetchCard();
  },

  watch: {
    cardOwnerType: {
      handler(newValue, oldValue) {
        if (newValue === 'new') {
          this.form.owner = {};
        }
      },
      immediate: true,
    },
    tab(newTab) {
      if (newTab === 2 && this.form.element_id) {
        this.fetchItems();
      }
    }
  },

  methods: {
    setAccessLevelsFromGroup(group_id) {
      this.form.access_level_ids = [];
      this.cardAccessLevelsGroups.map((item) => {
        if (item.group_id == group_id) {
          this.form.access_level_ids = JSON.parse(JSON.stringify(item.access_level_ids));
        }
      });

      //this.setNewAccessLevels();
    },

    parseSearchQueries() {
      return {
        name: this.$route.query.name || '',
      };
    },

    async fetchCard() {
      this.loading = true;
      try {
        const response = await this.$http.get(`cards/details/${this.$route.params.id || 0}`);
        this.form = response.card;
        if (this.$route.params.ownerId) {
          this.form.owner_id = this.$route.params.ownerId;
        }
        this.formSnapshot = JSON.stringify(this.form);
        this.accessLevels = response.access_levels;
        this.cardStatuses = response.card_statuses;
        this.defaultStatus = response.default_status;
        this.cardOwners = response.card_owners;
        //this.setNewAccessLevels();
        this.assets = response.card.assets;
        this.cardAccessLevelsGroups = response.card_access_levels_groups;
        this.cardTemplates = response.card_templates;
        this.defaultTemplate |= response.default_template;
        this.form.template_id = this.defaultTemplate?.id;
        this.form.card_number = this.form.card_number || response.next_card_number;
        if (this.form.status === 'printReady') {
          this.showPrintIcon = true;
        }
        // if (this.form.element_id) {
        //   // this.getLatestLogs();
        //   //this.getLatestParkingPenalties();
        //   if (this.form.status === 'idReading') this.watchCardReader();
        // }
        if (this.form.element_id === 0 && !this.$route.params.id) {
          this.form.owner.element_id = this.$route.params.id;
        }
        this.formSnapshot = this.lodash.cloneDeep(this.form);
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    /*setNewAccessLevels() {
      var _this = this;
      this.newAccessLevels = [];
      this.accessLevels.map(function (al) {
        if (_this.form.access_level_ids.indexOf(al.value) < 0)
          _this.newAccessLevels.push(al);
      });
      setTimeout(function () {
        if (_this.$refs["newAccessLevelId"] != undefined)
          _this.$refs["newAccessLevelId"].reload();
      }, 100);
    },*/

    async getLatestParkingPenalties(limit) {
      if (limit === undefined) {
        limit = 10;
      }
      try {
        const data = {
          limit,
          params: `"card_id":${this.$route.params.id}`,
          card_id: this.$route.params.id,
          active: 1,
        };
        const response = await this.$http.post('parking_penalties/', data);
        this.parking_penalties = response.parking_penalties;
        for (const penalty of this.parking_penalties) {
          penalty.editing = false;
        }
      } catch (e) {
        console.error(e);
      }
    },

    async saveParkingPenalty(penalty) {
      this.errors = {};
      try {
        let data = {
          card_id: this.$route.params.id,
          start_date: penalty.start_date,
          interval: penalty.interval,
          reason: penalty.reason,
        };
        const response = await this.$http.post('parking_penalties/save/', data);
        if (response.errors) {
          this.errors = response.errors;
        } else {
          this.getLatestParkingPenalties();
          this.parkingPenalty = {
            start_date: '',
            interval: '1 MONTH',
            reason: '',
          };
        }
      } catch (e) {
        console.error(e);
      }
    },

    async editParkingPenalty(data) {
      this.errors = [];
      try {
        // const datapost = {
        //   id: data.id,
        //   start_date: data.start_date,
        //   end_date: data.end_date,
        //   reason: data.reason,
        // };
        const response = await this.$http.post('parking_penalties/edit/');
        if (response.errors.length > 0) {
          this.errors = response.errors;
        } else {
          this.getLatestParkingPenalties();
        }
      } catch (e) {
        console.error(e);
      }
    },

    async deleteParkingPenalty(id) {
      try {
        const data = {
          id,
        };
        await this.$http.post('parking_penalties/delete/', data);
        this.getLatestParkingPenalties();
      } catch (e) {
        console.error(e);
      }
    },

    async save() {
      // for (const [index, plate] of this.form.plates.entries()) {
      //   plate.input_id = `plate-${index}`;
      // }

      const data = this.lodash.cloneDeep(this.form);
      delete data.log;
      delete data.status;
      data.deleted_access_level_ids = this.lodash
        .differenceWith(
          this.formSnapshot.access_levels,
          this.form.access_levels,
          (a, b) => a.element_id === b.element_id
        )
        .map((e) => e.element_id);
      data.updated_access_levels = this.lodash.differenceWith(
        this.form.access_levels,
        this.formSnapshot.access_levels,
        (a, b) => a.element_id === b.element_id && a.valid_to === b.valid_to
      );
      data.deleted_plates = this.lodash.differenceWith(
        this.formSnapshot.plates,
        this.form.plates,
        (a, b) => a.id === b.id
      );

      this.loading = true;
      this.errors = {};
      try {
        const response = await this.$http.post('cards/save', data);
        if (this.lodash.size(response.errors) > 0) {
          this.errors = response.errors;
          return;
        }

        if (response.status === 'OK') {
          this.$dialog.notify.success('Sikeres mentés');
          if (this.$route.name === 'NewCard') {
            this.$router.push({ name: 'CardEditor', params: { id: response.card.element_id } });
          } else {
            this.fetchCard();
          }
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    async print() {
      this.loading = true;
      try {
        const response = await this.$http.post('card/print/', {
          card_id: this.form.element_id,
        });
        printJS(`generated_cards/${response.filename}`);
        const result = await this.$dialog.confirm({
          text: 'Sikerült a kártyanyomtatás?',
          title: 'Figyelem!',
          color: 'primary',
        });
        if (result) {
          this.activate(true);
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    async suspend() {
      const result = await this.$dialog.confirm({
        text: 'Biztosan fel szeretné függeszteni a kártyát?',
        title: 'Figyelem!',
        color: 'primary',
      });
      if (result) {
        this.loading = true;
        try {
          const data = {
            element_id: this.form.element_id,
          };
          await this.$http.post('cards/suspend', data);
          this.fetchCard();
        } catch (e) {
          console.error(e);
        } finally {
          this.loading = false;
        }
      }
    },

    async deactivate() {
      const result = await this.$dialog.confirm({
        text: 'Ez a művelet törli a kártya hozzáférési szintjeit, ezáltal nem visszavonható. Folytatja?',
        title: 'Figyelem!',
        color: 'error',
      });
      if (result) {
        this.loading = true;
        try {
          const data = {
            element_id: this.form.element_id,
          };
          await this.$http.post('cards/deactivate', data);
          this.fetchCard();
        } catch (e) {
          console.error(e);
        } finally {
          this.loading = false;
        }
      }
    },

    async activate(skipDialog = false) {
      const result =
        skipDialog ||
        (await this.$dialog.confirm({
          text: 'Biztosan aktiválni szeretné a kártyát?',
          title: 'Figyelem!',
          color: 'primary',
        }));
      if (result) {
        this.loading = true;
        try {
          const data = {
            element_id: this.form.element_id,
          };
          await this.$http.post('cards/activate', data);
          this.fetchCard();
        } catch (e) {
          console.error(e);
        } finally {
          this.loading = false;
        }
      }
    },

    async fetchItems() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.loading = true;
      try {
        const response = await this.$http.post('assets/list', this.postDatas, {
          signal: this.abortController.signal,
        });
        this.dataTable.items = response.assets;
        this.dataTable.itemsLength = response.assets_count;
        this.dataTable.headers = [
          { text: 'Név', value: 'name' },
          { text: 'Szekrények', value: 'cabinets', sortable: false },
          { text: 'Műveletek', value: 'actions', sortable: false, width: '150px' },
        ];
      } catch (e) {
        if (e.name !== 'AbortError') {
          console.error(e);
        }
      } finally {
        this.loading = false;
      }
    },
  },

  computed: {
    page() {
      switch (this.$route.name) {
        case 'NewCard':
          return {
            isNew: true,
            title: 'Új kártya',
            breadcrumbItem: {
              text: 'Új kártya',
              disabled: true,
            },
          };
        case 'CardEditor':
          return {
            isNew: false,
            title: `Kártya: ${this.form.card_number ?? 'Betöltés...'}`,
            subtitle: `Tulajdonos: ${this.cardOwnerName ?? this.form.owner?.name ?? 'Betöltés...'}`,
            breadcrumbItem: {
              text: this.form.card_number,
              disabled: true,
            },
          };
      }
      return {};
    },


    searchQueries() {
      return {
        name: this.assetSearch.name || '',
      };
    },

    postDatas() {
      return {
        ...this.postData,
        card_id: this.form.element_id,
      };
    },

    extraFields() {
      return this.$store.getters['fields/findByModelName']('CardOwners');
    },

    isOwnerIdMutable() {
      return this.$route.name === 'NewCard' || this.hasFeature('CARD_OWNERID_CHANGE');
    },

    assignedAccessLevels() {
      return this.lodash.filter(this.accessLevels, (al) => {
        console.log(this.form.access_level_ids);
        return this.lodash.includes(this.form.access_level_ids, al.element_id);
      });
      // const accessLevels = [];
      // for (const accessLevel of this.accessLevels) {
      //   if (this.form.access_level_ids.includes(accessLevel.element_id)) {
      //     accessLevels.push(accessLevel);
      //   }
      // }
      // console.log(accessLevels);
      // return accessLevels;
    },

    isDirty() {
      return JSON.stringify(this.form) === this.formSnapshot;
    },
  },
};
</script>
